import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { LOGO_URL } from "@/lib/api";
import { DIRECT_BOOK } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarDays, Bed, CreditCard, Heart, Check } from "lucide-react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function DirectBook() {
  const { slug } = useParams();
  const [prop, setProp] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [ci, setCi] = useState("");
  const [co, setCo] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [wishlistOffer, setWishlistOffer] = useState(false);
  const [wishlistEmail, setWishlistEmail] = useState("");
  const [wishlistSaved, setWishlistSaved] = useState(false);
  const [wishlistBusy, setWishlistBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${BASE}/api/public/book/${slug}`)
      .then((r) => setProp(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const requestQuote = async (e) => {
    e.preventDefault();
    setBusy(true);
    setQuote(null);
    setWishlistOffer(false);
    setWishlistSaved(false);
    try {
      const { data } = await axios.post(
        `${BASE}/api/public/book/${slug}/quote`,
        { check_in: ci, check_out: co },
      );
      setQuote(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't get a quote";
      toast.error(detail);
      // Session Z21 — the quote endpoint 409s when the slot overlaps a
      // confirmed booking. Offer to save the guest's email so we can ping
      // them the moment the window opens up again.
      const status = err?.response?.status;
      const looksTaken = status === 409
        || /already|unavail|taken|overlap|booked/i.test(detail);
      if (looksTaken) {
        setWishlistOffer(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const saveWishlist = async () => {
    if (!wishlistEmail || !wishlistEmail.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setWishlistBusy(true);
    try {
      await axios.post(`${BASE}/api/public/book-wishlist`, {
        email: wishlistEmail,
        property_id: prop?.property_id || prop?.id,
        check_in: ci,
        check_out: co,
      });
      setWishlistSaved(true);
      toast.success("You're on the list — we'll ping you when it opens up.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save your email");
    } finally {
      setWishlistBusy(false);
    }
  };

  const startCheckout = async () => {
    if (!name || !email) {
      toast.error("Please add your name and email");
      return;
    }
    setCheckingOut(true);
    try {
      const { data } = await axios.post(
        `${BASE}/api/public/book/${slug}/checkout`,
        {
          check_in: ci,
          check_out: co,
          guest_name: name,
          guest_email: email,
          guest_phone: phone,
          origin_url: window.location.origin,
        },
      );
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout");
      setCheckingOut(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-8">
        <div className="ns-card p-10 text-center max-w-md">
          <div className="font-serif text-3xl">Not available</div>
          <p className="text-sm text-slate-500 mt-3">
            Direct booking isn't open for this unit yet. Please try Airbnb or Booking.com.
          </p>
        </div>
      </div>
    );
  }
  if (!prop) return null;

  return (
    <div data-testid={DIRECT_BOOK.root} className="min-h-screen bg-stone-50">
      <div
        className="h-64 md:h-80 relative bg-slate-800"
        style={
          prop.hero_image_url
            ? {
                backgroundImage: `url(${prop.hero_image_url})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : undefined
        }
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/70" />
        <div className="relative max-w-3xl mx-auto px-6 h-full flex flex-col justify-end pb-8 text-white">
          <div className="flex items-center gap-3 mb-6">
            <img src={LOGO_URL} alt="" className="w-10 h-10 rounded-lg ring-1 ring-white/30" />
            <div className="text-[11px] tracking-[0.24em] uppercase text-teal-200/90">
              Newport Stays · Direct booking
            </div>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl leading-tight drop-shadow">
            {prop.property_name}
          </h1>
          <p className="mt-2 text-white/80 text-sm inline-flex items-center gap-3">
            <Bed className="w-4 h-4" /> {prop.bedrooms} bed
            <span>·</span> {prop.address}
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-10 space-y-6">
        <form onSubmit={requestQuote} className="ns-card p-6 md:p-8 space-y-5">
          <div>
            <div className="ns-label">From £{Math.round(prop.nightly_rate)} / night</div>
            <div className="font-serif text-2xl mt-1">Check availability</div>
            <div className="text-xs text-slate-500 mt-1">
              Minimum stay {prop.min_nights} nights · Cleaning fee £{Math.round(prop.cleaning_fee)}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="ns-label">Check-in</Label>
              <Input
                data-testid={DIRECT_BOOK.checkIn}
                type="date"
                className="mt-2 h-11 bg-white"
                value={ci}
                onChange={(e) => setCi(e.target.value)}
                required
              />
            </div>
            <div>
              <Label className="ns-label">Check-out</Label>
              <Input
                data-testid={DIRECT_BOOK.checkOut}
                type="date"
                className="mt-2 h-11 bg-white"
                value={co}
                onChange={(e) => setCo(e.target.value)}
                required
              />
            </div>
          </div>
          <Button
            data-testid={DIRECT_BOOK.quoteBtn}
            type="submit"
            disabled={busy || !ci || !co}
            className="w-full h-11 rounded-full ns-btn-primary text-sm tracking-wide"
          >
            <CalendarDays className="w-4 h-4 mr-2" />
            {busy ? "Checking…" : "Get a quote"}
          </Button>
        </form>

        {wishlistOffer && !quote && (
          <div
            data-testid="directbook-wishlist-offer"
            className="ns-card p-6 md:p-8 border-l-4 border-amber-400 bg-amber-50/40"
          >
            {wishlistSaved ? (
              <div className="flex items-start gap-3 text-sm text-emerald-800">
                <Check className="w-5 h-5 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">You're on the wishlist.</div>
                  <div className="text-xs text-emerald-700 mt-1">
                    We'll email <strong>{wishlistEmail}</strong> the moment {prop.property_name} has an open {(() => {
                      try {
                        const a = new Date(ci); const b = new Date(co);
                        const nights = Math.max(1, Math.round((b - a) / 86400000));
                        return `${nights}-night`;
                      } catch { return "matching"; }
                    })()} window. No password needed.
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start gap-3">
                  <Heart className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium text-slate-900">Those nights are gone — want first dibs when they open up again?</div>
                    <div className="text-xs text-slate-600 mt-1">
                      Drop your email and we'll email you the moment {prop.property_name} has an open window matching your dates. No spam, one alert only.
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-[1fr_auto] gap-3 mt-4">
                  <Input
                    data-testid="directbook-wishlist-email"
                    type="email"
                    value={wishlistEmail}
                    onChange={(e) => setWishlistEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="h-11 bg-white"
                  />
                  <Button
                    data-testid="directbook-wishlist-save"
                    onClick={saveWishlist}
                    disabled={wishlistBusy}
                    className="h-11 px-5 rounded-full ns-btn-primary text-sm"
                  >
                    {wishlistBusy ? "Saving…" : "Save my email"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {quote && (
          <div data-testid={DIRECT_BOOK.totalRow} className="ns-card p-6 md:p-8 space-y-5">
            <div>
              <div className="ns-label">Your quote</div>
              <div className="grid grid-cols-[1fr_auto] gap-y-2 mt-3 text-sm">
                <div className="text-slate-500">
                  £{quote.nightly_rate} × {quote.nights} nights
                </div>
                <div>£{quote.subtotal}</div>
                <div className="text-slate-500">Cleaning fee</div>
                <div>£{quote.cleaning_fee}</div>
                <div className="pt-2 border-t border-slate-100 font-serif text-xl">Total</div>
                <div className="pt-2 border-t border-slate-100 font-serif text-xl">£{quote.total}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="ns-label">Full name</Label>
                <Input
                  className="mt-2 h-11 bg-white"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label className="ns-label">Email</Label>
                <Input
                  type="email"
                  className="mt-2 h-11 bg-white"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="md:col-span-2">
                <Label className="ns-label">Phone (optional)</Label>
                <Input
                  className="mt-2 h-11 bg-white"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>
            <Button
              onClick={startCheckout}
              disabled={checkingOut}
              className="w-full h-12 rounded-full ns-btn-primary tracking-wide"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {checkingOut ? "Redirecting…" : `Pay £${quote.total} securely`}
            </Button>
            <p className="text-[11px] text-slate-400 text-center">
              Powered by Stripe · Newport Stays never sees your card details.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
